import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Select, Input, DateTime } from '~/components/Form';
import { t } from '~/helpers';
import {
  storeContrato,
  updateContrato,
} from '~/store/modules/contrato/actions';

export default function CreateOrUpdate({
  reference,
  data,
  create,
  toggleConfirmButton,
}) {
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector((state) => state.contrato);

  const initials = create ? data : { ...find, ...data };

  async function handleSubmit(param) {
    try {
      const schema = Yup.object().shape({
        idcontrato: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(20, t('s.geral.validacao.tamanhomaximo').replace('%s', 20)),
        nomecontrato: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(50, t('s.geral.validacao.tamanhomaximo').replace('%s', 50)),
        datainicialcontrato: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        datafinalcontrato: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        statuscontrato: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(1, t('s.geral.validacao.tamanhomaximo').replace('%s', 1)),
      });

      await schema.validate(param, {
        abortEarly: false,
      });

      const objSend = {
        ...initials,
        ...param,
        idcliente: client,
        vigenteanteriorcontrato: 'S',
      };

      if (create) {
        await dispatch(storeContrato(objSend, params));
      } else {
        await dispatch(updateContrato(objSend, params));
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    toggleConfirmButton(true);
  }, []);

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
          <Input
            name="idcontrato"
            label={t('s.cadastro.contrato.idcontrato')}
          />

          <Input
            name="nomecontrato"
            label={t('s.cadastro.contrato.nomecontrato')}
          />

          <DateTime
            name="datainicialcontrato"
            label={t('s.cadastro.contrato.datainicialcontrato')}
            serialize={(value) =>
              value
                .set({ hour: 0, minute: 0, seconds: 0 })
                .format('DD/MM/YYYY HH:mm:ss')
            }
          />

          <DateTime
            name="datafinalcontrato"
            label={t('s.cadastro.contrato.datafinalcontrato')}
            serialize={(value) =>
              value
                .set({ hour: 23, minute: 59, seconds: 59 })
                .format('DD/MM/YYYY HH:mm:ss')
            }
          />

          <Select
            name="statuscontrato"
            label={t('s.cadastro.contrato.statuscontrato')}
            options={[
              { value: 'L', label: t('s.geral.opcoes.liberado') },
              { value: 'B', label: t('s.geral.opcoes.bloqueado') },
              { value: 'C', label: t('s.geral.opcoes.cancelado') },
            ]}
          />
        </Form>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
  toggleConfirmButton: PropTypes.func.isRequired,
};

CreateOrUpdate.defaultProps = {
  data: {},
};
